import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useItemDetail } from "@redotech/redo-customer-portal-shared/hooks/components/use-item-detail";
import { MerchantOverrides } from "@redotech/redo-customer-portal-shared/hooks/useReview/use-merchant-overrides";
import {
  ItemDetailSize,
  ReturnItemParams,
} from "@redotech/redo-customer-portal-shared/util";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { BadgePillThemeToColorMapping } from "@redotech/redo-web/tags/tag-pill";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import classNames from "classnames";
import { useContext } from "react";
import { SettingsContext } from "../../contexts/settings";
import {
  MerchantOverrideItemAdjustments,
  MerchantOverrideItemRejectMessage,
} from "../submission/screens/review/merchant-override-components";
import * as itemDetailCss from "./item-detail.module.css";

export function ItemDetail({
  item,
  isVertical,
  size = ItemDetailSize.MEDIUM,
  hideTextStrings = false,
  hideQuantity = false,
  hidePrice = false,
  overrides,
  isMerchant = false,
}: {
  item?: ReturnItemParams;
  isVertical?: boolean;
  size?: ItemDetailSize;
  hideTextStrings?: boolean;
  hideQuantity?: boolean;
  hidePrice?: boolean;
  overrides?: MerchantOverrides;
  isMerchant?: boolean;
}) {
  const screen = useScreenType(762);
  const shouldBeVertical = isVertical ?? screen === ScreenType.DESKTOP;
  const settings = useContext(SettingsContext);
  const {
    imageUri,
    texts,
    loading,
    formattedItemValue,
    formattedPrice,
    bonusBadge,
    returnTypeBadges,
    isTnplContractLineItem,
  } = useItemDetail({
    item,
    isVertical: shouldBeVertical,
    hideQuantity,
    settings,
  });

  if (loading) {
    return null;
  }
  const { title, subtitle, textStrings, badgeText } = texts;

  const gap = !shouldBeVertical
    ? "none"
    : size === ItemDetailSize.SMALL
      ? "xs"
      : "md";

  return (
    <Flex
      bgColor={item?.isPreRejected && !isMerchant ? "disabled" : "none"}
      flexDirection="column"
      gap="lg"
    >
      <Flex flex={1} justify="space-between">
        <Flex gap={shouldBeVertical ? "lg" : "xl"}>
          <Flex>
            {!!imageUri && (
              <img
                alt={title}
                className={classNames(itemDetailCss.image, {
                  [itemDetailCss.small]: size === ItemDetailSize.SMALL,
                })}
                src={imageUri}
                style={{ opacity: item?.isPreRejected ? 0.6 : 1 }}
              />
            )}
          </Flex>
          <Flex>
            <Flex
              dir="column"
              gap={gap}
              justify={shouldBeVertical ? "flex-start" : "center"}
            >
              <Text
                fontSize="md"
                fontWeight="medium"
                textColor={item?.isPreRejected ? "disabled" : "primary"}
              >
                {title}
              </Text>

              <Flex dir="column" gap="none">
                {subtitle && (
                  <Text
                    fontSize="xs"
                    fontWeight="regular"
                    key="subtitle"
                    textColor={item?.isPreRejected ? "disabled" : "secondary"}
                  >
                    {subtitle}
                  </Text>
                )}
                {!hideTextStrings &&
                  textStrings.map((text, index) => (
                    <Text
                      fontSize="xs"
                      fontWeight="regular"
                      key={index}
                      textColor={item?.isPreRejected ? "disabled" : "secondary"}
                    >
                      {text}
                    </Text>
                  ))}
                {item?.isPreRejected && isMerchant && (
                  <MerchantOverrideItemRejectMessage
                    merchantCreatedPreRejectMessage={
                      texts.merchantCreatedPreRejectMessage || ""
                    }
                  />
                )}
                {item?.isPreRejected && item.rejectMessage !== undefined ? (
                  <Text fontSize="xs" fontWeight="regular" textColor="disabled">
                    {item.rejectMessage}
                  </Text>
                ) : null}

                <Flex
                  dir="row"
                  gap="sm"
                  py={shouldBeVertical ? "md" : "xs"}
                  wrap="wrap"
                >
                  {bonusBadge ? (
                    <RedoBadge
                      color={BadgePillThemeToColorMapping[bonusBadge.color]}
                      text={bonusBadge.text}
                    />
                  ) : null}
                  {badgeText ? (
                    <RedoBadge color="success" text={badgeText} />
                  ) : null}
                  {returnTypeBadges?.map((badge, index) =>
                    badge.text.length >= 25 ? (
                      <Tooltip key={index} placement="top" title={badge.text}>
                        <div>
                          <RedoBadge
                            color={BadgePillThemeToColorMapping[badge.color]}
                            key={index}
                            text={
                              badge.text.length >= 25
                                ? badge.text.slice(0, 22) + "..."
                                : badge.text
                            }
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <RedoBadge
                        color={BadgePillThemeToColorMapping[badge.color]}
                        key={index}
                        text={badge.text}
                      />
                    ),
                  )}
                  {isTnplContractLineItem && (
                    <RedoBadge color="orange" text="Try First" />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {shouldBeVertical && !hidePrice ? (
          <Flex dir="row" gap="sm">
            {formattedPrice ? (
              <Tooltip placement="top" title={texts.discountExplanation}>
                <Flex dir="row" gap="xs">
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    style={{ textDecoration: "line-through" }}
                    textColor={item?.isPreRejected ? "disabled" : "secondary"}
                  >
                    {formattedPrice}
                  </Text>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    textColor={item?.isPreRejected ? "disabled" : "primary"}
                  >
                    {formattedItemValue}
                  </Text>
                </Flex>
              </Tooltip>
            ) : (
              <Text
                fontSize="sm"
                fontWeight="medium"
                textColor={item?.isPreRejected ? "disabled" : "primary"}
              >
                {formattedPrice || formattedItemValue}
              </Text>
            )}
          </Flex>
        ) : null}
      </Flex>
      {overrides && (
        <MerchantOverrideItemAdjustments
          itemReturnValue={
            item?.itemValue ? parseFloat(item.itemValue) : undefined
          }
          overrides={overrides}
          pendingReturnItemId={item?.pendingReturnItemId}
        />
      )}
    </Flex>
  );
}
